import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";

import { PaziaButton, PaziaInput } from "components/atoms";
import {
  IDropdownOption,
  PaziaCheckbox,
  PaziaFileDropzone,
  PaziaFormButton,
  PaziaSelect,
  PaziaTextarea,
} from "components/atoms";
import { CourseResourceApi } from "api";
import { useCourseDocumentsOptions } from "api/hooks";

interface ResourceFormProps {
  onSubmit: (data: ResourceFormValues) => Promise<void>;
  folderOptions: IDropdownOption[];
  resource?: CourseResourceApi;
  onCancel?: () => void;
  courseId: number;
}
export const ResourceForm = ({ onSubmit, courseId, folderOptions, onCancel, resource }: ResourceFormProps) => {
  const { loading, options } = useCourseDocumentsOptions(courseId);
  const methods = useForm({
    resolver: yupResolver(resourceSchema),
    defaultValues: {
      [RESOURCE_FORM_NAMES.ID]: resource?.id,
      [RESOURCE_FORM_NAMES.SEND_MAIL]: false,
      [RESOURCE_FORM_NAMES.NAME]: resource?.name,
      [RESOURCE_FORM_NAMES.FOLDER]: folderOptions.find((f) => f.value == resource?.folder)?.value?.toString() ?? "",
      [RESOURCE_FORM_NAMES.DESCRIPTION]: resource?.description ?? "",
      [RESOURCE_FORM_NAMES.TYPE]: resource?.type,
      [RESOURCE_FORM_NAMES.LINK]: resource?.link ?? "",
      [RESOURCE_FORM_NAMES.RESOURCE]: resource?.link ? [resource?.link] : [],
    },
  });
  const { handleSubmit, watch, reset, formState } = methods;
  const folderWatch = watch(RESOURCE_FORM_NAMES.FOLDER);
  const typeWatch = watch(RESOURCE_FORM_NAMES.TYPE);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3">
        {resource == null && (
          <>
            <PaziaSelect
              required
              label="Folder"
              name={RESOURCE_FORM_NAMES.FOLDER}
              options={[...folderOptions, { value: "-1", label: "Dodaj nowy ..." }]}
            />
            {folderWatch === "-1" && (
              <PaziaInput required name={RESOURCE_FORM_NAMES.NEW_FOLDER} label="Nazwa folderu" />
            )}
          </>
        )}
        <PaziaInput required name={RESOURCE_FORM_NAMES.NAME} label="Nazwa" />
        <PaziaTextarea name={RESOURCE_FORM_NAMES.DESCRIPTION} label="Opis" />
        <PaziaSelect
          required
          label="Typ"
          name={RESOURCE_FORM_NAMES.TYPE}
          options={COURSE_RESOURCE_TYPE_OPTIONS}
          disabled={resource != null}
        />
        {typeWatch == COURSE_RESOURCE_TYPE.INTERNAL_BLOB_FILE && (
          <PaziaFileDropzone type={["image", "pdf"]} name={RESOURCE_FORM_NAMES.RESOURCE} label="Plik" required />
        )}
        {LINKABLE_RESOURCE_TYPES.includes(typeWatch) && (
          <PaziaInput required name={RESOURCE_FORM_NAMES.LINK} label="Link" />
        )}
        {typeWatch == COURSE_RESOURCE_TYPE.DOCUMENT && (
          <PaziaSelect
            required
            name={RESOURCE_FORM_NAMES.DOCUMENT}
            label="Dokument"
            disabled={loading}
            options={options}
          />
        )}
        <PaziaCheckbox name={RESOURCE_FORM_NAMES.SEND_MAIL} label="Wyślij powiadomienia email" />
        <div className="flex justify-end gap-2">
          <PaziaButton
            onClick={() => {
              reset({});
              onCancel?.();
            }}
            title="Anuluj"
            className="mt-4"
            type="tertiary"
          />
          <PaziaFormButton title="Zapisz" className="mt-4" />
        </div>
      </form>
    </FormProvider>
  );
};

const RESOURCE_FORM_NAMES = {
  ID: "id",
  NAME: "name",
  DESCRIPTION: "description",
  RESOURCE: "resource",
  TYPE: "type",
  LINK: "link",
  DOCUMENT: "document",
  FOLDER: "folder",
  NEW_FOLDER: "newFolder",
  SEND_MAIL: "sendMail",
} as const;

export const COURSE_RESOURCE_TYPE = {
  EXTERNAL_LINK: 1,
  INTERNAL_BLOB_FILE: 2,
  YOU_TUBE_VIDEO_THUMBNAIL: 3,
  YOU_TUBE_VIDEO_EMBED: 4,
  DOCUMENT: 5,
} as const;

export const LINKABLE_RESOURCE_TYPES = [
  COURSE_RESOURCE_TYPE.EXTERNAL_LINK,
  COURSE_RESOURCE_TYPE.YOU_TUBE_VIDEO_THUMBNAIL,
  COURSE_RESOURCE_TYPE.YOU_TUBE_VIDEO_EMBED,
] as number[];
export const COURSE_RESOURCE_TYPE_OPTIONS = [
  { label: "Link zewnętrzny", value: COURSE_RESOURCE_TYPE.EXTERNAL_LINK },
  { label: "Plik", value: COURSE_RESOURCE_TYPE.INTERNAL_BLOB_FILE },
  { label: "YouTube - zadokowany", value: COURSE_RESOURCE_TYPE.YOU_TUBE_VIDEO_EMBED },
  { label: "YouTube - miniatura", value: COURSE_RESOURCE_TYPE.YOU_TUBE_VIDEO_THUMBNAIL },
  { label: "Dokument", value: COURSE_RESOURCE_TYPE.DOCUMENT },
];

const resourceSchema = yup
  .object({
    [RESOURCE_FORM_NAMES.ID]: yup.number().nullable(),
    [RESOURCE_FORM_NAMES.NAME]: yup.string().required(),
    [RESOURCE_FORM_NAMES.DESCRIPTION]: yup.string(),
    [RESOURCE_FORM_NAMES.TYPE]: yup.number().required(),
    [RESOURCE_FORM_NAMES.LINK]: yup.string().when(RESOURCE_FORM_NAMES.TYPE, {
      is: (type: number) => LINKABLE_RESOURCE_TYPES.includes(type),
      then: (shema) => shema.required(),
    }),
    [RESOURCE_FORM_NAMES.DOCUMENT]: yup.number().when(RESOURCE_FORM_NAMES.TYPE, {
      is: (type: number) => type == COURSE_RESOURCE_TYPE.DOCUMENT,
      then: (shema) => shema.required(),
    }),
    [RESOURCE_FORM_NAMES.RESOURCE]: yup
      .array()
      .of(yup.mixed())
      .when(RESOURCE_FORM_NAMES.TYPE, {
        is: (type: number) => type == COURSE_RESOURCE_TYPE.INTERNAL_BLOB_FILE,
        then: (shema) => shema.required(),
      }),
    [RESOURCE_FORM_NAMES.FOLDER]: yup.string().when(RESOURCE_FORM_NAMES.ID, {
      is: (id: number) => !id,
      then: (shema) => shema.required(),
    }),
    [RESOURCE_FORM_NAMES.NEW_FOLDER]: yup.string().when(RESOURCE_FORM_NAMES.FOLDER, {
      is: "-1",
      then: (shema) => shema.required(),
    }),
    [RESOURCE_FORM_NAMES.SEND_MAIL]: yup.boolean().required().default(true),
  })
  .required();

export type ResourceFormValues = yup.InferType<typeof resourceSchema>;
